import React, { useState, useEffect, useRef } from 'react';
import { sessionsAPI, assessmentsAPI } from '../utils/api';
import io from 'socket.io-client';
import toastr from 'toastr';
import './ChallengeLab.css';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'https://api.sechire.net';

function ChallengeLab({ session: initialSession, interview, user, onSessionEnd, assessmentId }) {
  const [session, setSession] = useState(initialSession);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flagInput, setFlagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [flagResult, setFlagResult] = useState(null);
  const [hints, setHints] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [labTimeRemaining, setLabTimeRemaining] = useState(null);
  const [containerExpired, setContainerExpired] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [progress, setProgress] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [showNextChallengeButton, setShowNextChallengeButton] = useState(false);
  const [loadingNextChallenge, setLoadingNextChallenge] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pausingOrResuming, setPausingOrResuming] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);

  const socketRef = useRef(null);
  const iframeRef = useRef(null);
  const labWindowRef = useRef(null);

  useEffect(() => {
    initializeSession();
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (session?.expiresAt) {
      const interval = setInterval(() => {
        const now = new Date();
        const expires = new Date(session.expiresAt);
        const remaining = expires - now;

        if (remaining <= 0) {
          handleTimeout();
        } else {
          setTimeRemaining(remaining);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [session]);

  // Lab Time countdown (container expiry timer)
  useEffect(() => {
    if (session?.containerExpiresAt) {
      const interval = setInterval(() => {
        const now = new Date();
        const expires = new Date(session.containerExpiresAt);
        const remaining = expires - now;

        if (remaining <= 0) {
          setLabTimeRemaining(0);
          setContainerExpired(true);
          console.log('[Lab Time] Container has expired');
        } else {
          setLabTimeRemaining(remaining);
          setContainerExpired(false);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [session?.containerExpiresAt]);

  const fetchAssessmentProgress = async () => {
    if (!assessmentId) return;

    try {
      const response = await assessmentsAPI.getProgress(assessmentId);
      setProgress(response.data.progress);
      console.log('Assessment progress:', response.data.progress);
    } catch (error) {
      console.error('Failed to fetch assessment progress:', error);
    }
  };

  const initializeSession = async () => {
    try {
      setLoading(true);

      // If no session provided, create one
      if (!initialSession) {
        // In real implementation, would get challengeId from interview
        const challengeId = interview.challenges?.[0];
        const newSession = await sessionsAPI.create(
          interview.id,
          user.id,
          challengeId
        );
        setSession(newSession.data.session);
      }

      // Fetch progress for multi-challenge assessments
      if (assessmentId) {
        await fetchAssessmentProgress();
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to initialize session:', error);
      toastr.error('Failed to start challenge. Please try again.');
      onSessionEnd();
    }
  };

  const setupWebSocket = () => {
    socketRef.current = io(SOCKET_URL, {
      auth: {
        token: localStorage.getItem('token')
      }
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to monitoring service');

      // Join interview room
      socketRef.current.emit('candidate:join', {
        interviewId: interview.id,
        candidateId: user.id
      });
    });

    socketRef.current.on('interviewer:joined', (data) => {
      addActivityLog('Interviewer joined the session', 'info');
    });

    socketRef.current.on('interviewer:note', (data) => {
      // Interviewer made a note - we don't show to candidate
      console.log('Interviewer note recorded');
    });

    socketRef.current.on('disconnect', () => {
      console.log('Disconnected from monitoring service');
    });
  };

  const trackAction = (action, data = {}) => {
    if (socketRef.current) {
      socketRef.current.emit('candidate:action', {
        interviewId: interview.id,
        candidateId: user.id,
        sessionId: session?.sessionId,
        action,
        data,
        timestamp: new Date()
      });
    }
  };

  const addActivityLog = (message, type = 'action') => {
    setActivityLog(prev => [...prev, {
      message,
      type,
      timestamp: new Date()
    }]);
  };

  const handleFlagSubmit = async (e) => {
    e.preventDefault();
    if (!flagInput.trim()) return;

    // Check if assessment deadline has passed
    if (timeRemaining !== null && timeRemaining <= 0) {
      setFlagResult({
        correct: false,
        message: 'Assessment deadline has passed. Flag submissions are no longer accepted.'
      });
      addActivityLog('Flag submission blocked - assessment deadline expired', 'error');
      return;
    }

    setSubmitting(true);
    setFlagResult(null);

    try {
      // Track submission attempt
      trackAction('flag_submit', { flag: flagInput });
      addActivityLog(`Submitted flag: ${flagInput}`, 'flag');

      // Submit flag to backend
      const response = await sessionsAPI.submitFlag(session.sessionId, flagInput);

      // Backend returns: { success: true, correct: true/false, message: string }
      const result = response.data;
      setFlagResult(result);

      if (result.correct) {
        addActivityLog('Flag correct! Challenge completed!', 'success');

        // Update session state if challenge completed
        if (result.completed) {
          // Check if this is a multi-challenge assessment
          if (assessmentId && progress) {
            // Refresh progress to see if there are more challenges
            await fetchAssessmentProgress();

            // Check if there are more challenges
            const updatedProgress = await assessmentsAPI.getProgress(assessmentId);
            const progressData = updatedProgress.data.progress;

            if (progressData.currentChallengeIndex < progressData.totalChallenges) {
              // More challenges available
              setShowNextChallengeButton(true);
              addActivityLog('Challenge complete! Click Next Challenge to continue.', 'info');
            } else {
              // All challenges completed
              setTimeout(() => {
                handleSessionComplete();
              }, 3000);
            }
          } else {
            // Single challenge - complete session
            setTimeout(() => {
              handleSessionComplete();
            }, 3000);
          }
        }
      } else {
        addActivityLog('Flag incorrect. Try again!', 'error');
      }

      setFlagInput('');
    } catch (error) {
      console.error('Failed to submit flag:', error);
      setFlagResult({ correct: false, message: 'Submission failed' });
      addActivityLog('Flag submission failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextChallenge = async () => {
    if (!assessmentId) return;

    setLoadingNextChallenge(true);
    setShowNextChallengeButton(false);

    try {
      // Complete current challenge in backend
      const sessionResults = {
        sessionId: session.sessionId,
        score: 100, // Will be calculated by backend
        flagsCorrect: 1,
        flagsSubmitted: session.flagsSubmitted || 1,
        hintsUsed: hints.length,
        timeSpent: session.timeSpent || 0
      };

      const response = await assessmentsAPI.completeChallenge(assessmentId, sessionResults);

      // Get next challenge
      const nextChallengeResponse = await assessmentsAPI.getCurrentChallenge(assessmentId);

      if (nextChallengeResponse.data.currentChallenge) {
        // Load next challenge
        addActivityLog(`Loading next challenge...`, 'info');

        // Update progress
        setProgress(nextChallengeResponse.data.progress);

        // Reset state for new challenge
        setFlagResult(null);
        setFlagInput('');
        setHints([]);

        // Start new session for next challenge
        // This would need to be implemented based on your session creation logic
        // For now, we'll refresh the page or notify to restart
        addActivityLog('Starting next challenge...', 'info');

        // Reload component with new challenge
        window.location.reload();
      } else {
        // All challenges completed
        addActivityLog('All challenges completed!', 'success');
        setTimeout(() => {
          handleSessionComplete();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to load next challenge:', error);
      toastr.error('Failed to load next challenge. Please try again.');
      setShowNextChallengeButton(true);
    } finally {
      setLoadingNextChallenge(false);
    }
  };

  const handleHintRequest = async () => {
    try {
      const hintLevel = hints.length + 1;
      trackAction('hint_request', { hintLevel });
      addActivityLog(`Requested hint level ${hintLevel}`, 'hint');

      // Get available hints from challenge data to find the hintId for the next level
      const challengeData = session?.challenge || session?.challengeId;
      const availableHints = challengeData?.hints || [];
      const nextHint = availableHints.find(h => h.level === hintLevel);

      if (!nextHint) {
        toastr.warning('No more hints available');
        return;
      }

      const response = await sessionsAPI.useHint(session.sessionId, nextHint._id);

      setHints(prev => [...prev, {
        level: hintLevel,
        text: response.data.hint,
        penalty: response.data.penalty || 0
      }]);

      addActivityLog(`Hint ${hintLevel} received (-${response.data.penalty || 0} pts)`, 'hint');
    } catch (error) {
      console.error('Failed to get hint:', error);

      if (error.response?.status === 404) {
        toastr.warning(error.response.data.message || 'No more hints available');
      } else if (error.response?.status === 429) {
        toastr.warning('Too many requests. Please wait before trying again.');
      } else {
        toastr.error('Failed to retrieve hint');
      }
    }
  };

  const handleSessionComplete = async () => {
    try {
      await sessionsAPI.complete(session.sessionId);
      trackAction('session_complete');
      toastr.success('Challenge completed! Redirecting to dashboard...');
      onSessionEnd();
    } catch (error) {
      console.error('Failed to complete session:', error);
    }
  };

  const handleTimeout = () => {
    toastr.warning('Session time expired!');
    onSessionEnd();
  };

  const handleOpenLab = () => {
    if (session?.sessionUrl) {
      // Open lab in new window and track the reference
      labWindowRef.current = window.open(session.sessionUrl, '_blank', 'noopener,noreferrer');
      trackAction('lab_window_opened');
    }
  };

  const handleExitClick = () => {
    setShowExitDialog(true);
  };

  const handleExitConfirm = async () => {
    try {
      // Close lab window if it exists
      if (labWindowRef.current && !labWindowRef.current.closed) {
        labWindowRef.current.close();
      }

      // Terminate session on backend (stops container)
      if (session?.sessionId) {
        await sessionsAPI.terminate(session.sessionId);
        trackAction('session_terminated');
      }

      setShowExitDialog(false);
      onSessionEnd();
    } catch (error) {
      console.error('Failed to terminate session:', error);
      // Still exit even if termination fails
      setShowExitDialog(false);
      onSessionEnd();
    }
  };

  const handleExitCancel = () => {
    setShowExitDialog(false);
  };

  const handlePauseResume = async () => {
    if (pausingOrResuming) return;

    try {
      setPausingOrResuming(true);

      if (isPaused || containerExpired) {
        // Resume session (creates fresh container)
        const response = await sessionsAPI.resume(session.sessionId);
        setSession(response.data.session);
        setIsPaused(false);
        setContainerExpired(false); // Reset expired state
        setIframeKey(prev => prev + 1); // Force iframe reload with new container
        trackAction('session_resumed');

        const message = isPaused
          ? `Session resumed! You were paused for ${Math.floor(response.data.pausedDuration / 60)} minutes.`
          : 'Lab restarted with fresh environment!';

        addActivityLog(message, 'info');
        toastr.success(message);
      } else {
        // Pause session
        const response = await sessionsAPI.pause(session.sessionId);
        setSession(response.data.session);
        setIsPaused(true);
        trackAction('session_paused');
        addActivityLog('Session paused', 'info');
      }
    } catch (error) {
      console.error('Failed to pause/resume session:', error);
      const errorMsg = error.response?.data?.error || 'Failed to pause/resume session';
      toastr.error(errorMsg);
    } finally {
      setPausingOrResuming(false);
    }
  };

  const handleIframeLoad = () => {
    trackAction('lab_loaded');
    addActivityLog('Challenge environment loaded', 'info');
  };

  const formatTime = (ms) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="challenge-lab loading">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Preparing your challenge environment...</p>
          <p className="loading-detail">This may take up to 30 seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="challenge-lab">
      {/* Header */}
      <header className="lab-header">
        <div className="lab-title">
          <h1>{challenge?.title || interview.position}</h1>
          <span className="challenge-category">{challenge?.category || 'Security Challenge'}</span>
          {progress && progress.totalChallenges > 1 && (
            <div className="challenge-progress-indicator">
              <span className="progress-text">
                Challenge {progress.currentChallengeIndex + 1} of {progress.totalChallenges}
              </span>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="lab-controls">
          {timeRemaining && (
            <div className={`time-remaining ${timeRemaining < 300000 ? 'warning' : ''}`}>
              <span className="label">Assessment Time:</span>
              <span className="time">{formatTime(timeRemaining)}</span>
            </div>
          )}
          {labTimeRemaining !== null && !containerExpired && (
            <div className={`time-remaining ${labTimeRemaining < 300000 ? 'warning' : ''}`}>
              <span className="label">Lab Time:</span>
              <span className="time">{formatTime(labTimeRemaining)}</span>
            </div>
          )}
          {session?.assessmentId?.mode === 'async' && (
            <button
              className={`btn ${isPaused ? 'btn-success' : 'btn-warning'}`}
              onClick={handlePauseResume}
              disabled={pausingOrResuming}
            >
              {pausingOrResuming ? '...' : (isPaused ? 'Resume' : 'Pause')}
            </button>
          )}
          <button className="btn btn-danger" onClick={handleExitClick}>
            Exit Lab
          </button>
        </div>
      </header>

      {/* Paused Overlay */}
      {isPaused && (
        <div className="paused-overlay">
          <div className="paused-message">
            <h2>Assessment Paused</h2>
            <p>Click Resume when you're ready to continue</p>
            <button
              className="btn btn-primary btn-large"
              onClick={handlePauseResume}
              disabled={pausingOrResuming}
            >
              {pausingOrResuming ? 'Resuming...' : 'Resume Assessment'}
            </button>
          </div>
        </div>
      )}

      {/* Exit Confirmation Dialog */}
      {showExitDialog && (
        <div className="paused-overlay">
          <div className="paused-message">
            <h2>Exit Lab?</h2>
            <p className="exit-message">
              Are you sure you want to exit? This will:
            </p>
            <ul style={{ textAlign: 'left', marginTop: '1rem', color: 'var(--text-secondary)' }}>
              <li>Close the lab window</li>
              <li>Stop the lab environment</li>
              <li>End your current session</li>
            </ul>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={handleExitCancel}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleExitConfirm}>
                Exit Lab
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="lab-content">
        {/* Main Lab Area */}
        <div className="lab-main">
          {/* Challenge Description */}
          <div className="challenge-description">
            <h2>Challenge Objective</h2>
            <p>{challenge?.description || 'Find and exploit the vulnerability in the environment below. Submit the flag when you find it.'}</p>

            {challenge?.scenario && (
              <div className="scenario">
                <h3>Scenario</h3>
                <p>{challenge.scenario}</p>
              </div>
            )}
          </div>

          {/* Lab Environment */}
          <div className="lab-environment">
            <div className="environment-header">
              <h3>Live Environment</h3>
              <span className={`status-indicator ${containerExpired ? 'offline' : 'online'}`}>
                {containerExpired ? 'Lab Expired' : 'Running'}
              </span>
            </div>
            <div className="lab-access-card">
              {containerExpired ? (
                <div className="lab-expired">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <div>
                    <h4>Lab Environment Expired</h4>
                    <p>Your lab environment has reached its time limit and has been stopped.</p>
                    <p style={{ marginTop: '0.5rem' }}>Click "Resume Lab" below to get a fresh environment with a new time window.</p>
                  </div>
                  <button
                    className="btn btn-success btn-large"
                    onClick={handlePauseResume}
                    disabled={pausingOrResuming}
                    style={{ marginTop: '1.5rem' }}
                  >
                    {pausingOrResuming ? 'Creating New Lab...' : 'Resume Lab (Fresh Environment)'}
                  </button>
                </div>
              ) : session?.sessionUrl ? (
                <div className="lab-ready">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-600)" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M9 3v18"/>
                    <path d="M15 9l-6 6"/>
                    <path d="M15 15l-6-6"/>
                  </svg>
                  <div>
                    <h4>Your Challenge Lab is Ready</h4>
                    <p>The lab environment will open in a new tab. Keep this window open to submit your flag.</p>
                  </div>
                  <button
                    onClick={handleOpenLab}
                    className="btn btn-primary btn-large"
                    style={{ marginTop: '1rem' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    Open Lab in New Window
                  </button>
                </div>
              ) : (
                <div className="no-environment">
                  <p>Environment not available</p>
                </div>
              )}
            </div>
          </div>

          {/* Flag Submission */}
          <div className="flag-submission">
            <h3>Submit Flag</h3>
            {showNextChallengeButton ? (
              <div className="next-challenge-section">
                <p className="challenge-complete-message">
                  Challenge completed successfully! Ready for the next one?
                </p>
                <button
                  className="btn btn-primary btn-large"
                  onClick={handleNextChallenge}
                  disabled={loadingNextChallenge}
                >
                  {loadingNextChallenge ? 'Loading Next Challenge...' : 'Next Challenge'}
                </button>
              </div>
            ) : (
              <>
                {timeRemaining !== null && timeRemaining <= 0 && (
                  <div className="flag-result error" style={{ marginBottom: '1rem' }}>
                    Assessment deadline has passed. Flag submissions are no longer accepted.
                  </div>
                )}
                <form onSubmit={handleFlagSubmit}>
                  <div className="flag-input-group">
                    <input
                      type="text"
                      className="flag-input"
                      value={flagInput}
                      onChange={(e) => setFlagInput(e.target.value)}
                      placeholder="Enter flag here (e.g., FLAG{...})"
                      disabled={submitting || (timeRemaining !== null && timeRemaining <= 0)}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting || !flagInput.trim() || (timeRemaining !== null && timeRemaining <= 0)}
                    >
                      {timeRemaining !== null && timeRemaining <= 0
                        ? 'Deadline Expired'
                        : submitting
                          ? 'Checking...'
                          : 'Submit Flag'}
                    </button>
                  </div>
                </form>
                {flagResult && (
                  <div className={`flag-result ${flagResult.correct ? 'success' : 'error'}`}>
                    {flagResult.correct ? '✓ Correct!' : '✗ Incorrect'}
                    {flagResult.message && <p>{flagResult.message}</p>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lab-sidebar">
          {/* Hints */}
          <div className="hints-panel">
            <h3>Hints</h3>
            {hints.length === 0 ? (
              <p className="hint-info">Need help? Hints are available but may reduce your score.</p>
            ) : (
              <div className="hints-list">
                {hints.map((hint, index) => (
                  <div key={index} className="hint-item">
                    <div className="hint-header">
                      <span>Hint {hint.level}</span>
                      <span className="penalty">-{hint.penalty} pts</span>
                    </div>
                    <p>{hint.text}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="hint-buttons">
              <button
                className="btn btn-sm btn-hint"
                onClick={handleHintRequest}
                disabled={hints.length >= 3}
              >
                Request Hint {hints.length + 1}
              </button>
            </div>
          </div>

          {/* Activity Log */}
          <div className="activity-panel">
            <h3>Activity Log</h3>
            <div className="activity-list">
              {activityLog.slice(-10).reverse().map((log, index) => (
                <div key={index} className={`activity-item ${log.type}`}>
                  <span className="time">{log.timestamp.toLocaleTimeString()}</span>
                  <span className="message">{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default ChallengeLab;
